import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, MessageCircle } from "lucide-react";

const BridgeMark = ({ className }) => (
  <svg viewBox="0 0 36 28" fill="none" className={className} aria-hidden="true">
    <rect x="1.5" y="13" width="3" height="14" rx="1.5" fill="currentColor" />
    <rect x="31.5" y="13" width="3" height="14" rx="1.5" fill="currentColor" />
    <path d="M3 13 Q18 1 33 13" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    <line x1="0" y1="21" x2="36" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="10.5" y1="7" x2="10.5" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="18" y1="3" x2="18" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="25.5" y1="7" x2="25.5" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export default function Contact() {
  return (
    <main data-testid="contact-page" className="min-h-screen bg-[#FDF3EC]">
      {/* Minimal nav */}
      <nav className="border-b border-black/5 bg-[#FDF3EC]/95 px-6 py-4 backdrop-blur-sm md:px-12">
        <Link to="/" className="flex items-center gap-2.5 text-[#E8542A]">
          <BridgeMark className="h-6 w-8" />
          <span className="font-display text-xl font-bold tracking-tight">ClassBridge</span>
        </Link>
      </nav>

      <div className="mx-auto w-full max-w-2xl px-6 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/"
            data-testid="contact-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-[#8A6D5C] transition-colors hover:text-[#E8542A]"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>

          <h1
            data-testid="contact-title"
            className="font-display mt-6 text-4xl font-bold text-[#1A1918] md:text-5xl"
          >
            Contact Us
          </h1>
          <p className="mt-3 text-base leading-relaxed text-[#8A6D5C]">
            Questions, feedback, or partnership enquiries — we'd love to hear from you.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.12 }}
          className="mt-10 space-y-5"
        >
          {/* Primary email card */}
          <a
            data-testid="contact-email-link"
            href="mailto:pbp@class-bridge.com"
            className="group flex items-center gap-5 rounded-3xl border border-black/5 bg-white p-7 shadow-sm transition-all duration-200 hover:border-[#E8542A]/30 hover:shadow-md"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#E8542A]/10 transition-colors duration-200 group-hover:bg-[#E8542A]/15">
              <Mail className="h-5 w-5 text-[#E8542A]" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#8A6D5C]">Email us</p>
              <p
                data-testid="contact-email-address"
                className="mt-1 font-semibold text-[#1A1918] transition-colors duration-200 group-hover:text-[#E8542A]"
              >
                pbp@class-bridge.com
              </p>
              <p className="mt-0.5 text-xs text-[#A8968E]">
                We typically reply within 1 business day
              </p>
            </div>
          </a>

          {/* Feedback card */}
          <div className="rounded-3xl border border-black/5 bg-white p-7 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#E8542A]/10">
                <MessageCircle className="h-4 w-4 text-[#E8542A]" />
              </div>
              <p className="font-semibold text-[#1A1918]">Share your feedback</p>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-[#8A6D5C]">
              We're always working to improve ClassBridge for teachers across India. If you have
              suggestions, bug reports or ideas for new features, drop us a line at{" "}
              <a
                href="mailto:pbp@class-bridge.com"
                className="font-semibold text-[#E8542A] underline-offset-2 hover:underline"
              >
                pbp@class-bridge.com
              </a>
              .
            </p>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 py-2">
            <div className="h-px flex-1 bg-black/5" />
            <p className="text-xs text-[#A8968E]">ClassBridge · India</p>
            <div className="h-px flex-1 bg-black/5" />
          </div>

          <p className="text-center text-xs text-[#A8968E]">
            By ClassBridge ·{" "}
            <Link to="/privacy" className="transition-colors hover:text-[#E8542A]">
              Privacy Policy
            </Link>
          </p>
        </motion.div>
      </div>
    </main>
  );
}
